import React, { useCallback } from 'react';
import map from 'lodash/map';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import Filters from '../../Filters';
import {
  getDocumentsFilters,
  setDocumentsFilters,
} from '../../../screens/PatientCard/store';
import { clearDocumentsDefaultProjectId } from '../../../utils/documentsDefaultProjectId';

const FiltersToolbar = ({
  recipientId,
  presets,
  onFiltersDraftChange,
  extra,
}) => {
  const dispatch = useDispatch();
  const filters = useSelector(getDocumentsFilters);

  // <Filters> seeds its internal state from `value` on mount only, so it has to
  // remount whenever ConnectedFilters writes the filters itself (e.g. the
  // project default, which lands once enrolments load) or the stored filter and
  // the visible row drift apart.
  //
  // Those writes always mint fresh filter ids, whereas a user edit round-trips
  // the ids <Filters> already holds, so deriving the key from the ids remounts
  // for the former and leaves the user's own edits alone. Deriving it also
  // means a write that changes nothing produces the same key and no remount —
  // important, because remounting re-runs the effects that trigger these
  // writes.
  const key = `${recipientId}:${map(filters, 'id').join(',')}`;

  // Once the user manually changes (or resets) the filter, stop re-applying
  // the sticky project default on future reloads/tabs for this patient —
  // otherwise their own choice would keep getting silently overridden by it.
  const handleOnChange = useCallback(
    (newFilters) => {
      clearDocumentsDefaultProjectId(recipientId);
      dispatch(setDocumentsFilters(newFilters || []));
    },
    [dispatch, recipientId],
  );

  const handleOnReset = useCallback(() => {
    clearDocumentsDefaultProjectId(recipientId);
    dispatch(setDocumentsFilters([]));
  }, [dispatch, recipientId]);

  return (
    <div className="min-w-0">
      <Filters
        key={key}
        presets={presets}
        value={filters}
        fixedFirstFilterKey="projectId"
        allowConditionGroups={false}
        onChange={handleOnChange}
        onResetSavedFilters={handleOnReset}
        onFiltersDraftChange={onFiltersDraftChange}
        extra={extra}
      />
    </div>
  );
};

FiltersToolbar.propTypes = {
  recipientId: PropTypes.string,
  presets: PropTypes.arrayOf(PropTypes.object),
  onFiltersDraftChange: PropTypes.func,
  extra: PropTypes.node,
};

FiltersToolbar.defaultProps = {
  recipientId: null,
  presets: [],
  onFiltersDraftChange: null,
  extra: null,
};

export default FiltersToolbar;
